/**
 * 報酬セット → カテゴリー選択画面 (v3.2.6 / T5 Phase 2)。
 *
 * URL: /sales/commissions/:setId
 * - 商品カテゴリー (product_categories) を全件カードで表示
 * - 各カードに「設定済み / 一部設定 / 未設定」のバッジ
 *   - そのカテゴリ名と一致する catalog の items[] が
 *     セットの items[] にすべて含まれ、value > 0 ⇒ 設定済み
 *     一部のみ設定 ⇒ 一部設定
 *     全く未設定 ⇒ 未設定
 * - クリックでカテゴリ別編集画面へ遷移 (3 タブ: 紹介者/SA/社員)
 * - 下部に「このセットを有効化する商品カテゴリ」割当 UI
 */
import type { CommissionItem, CommissionListType } from '@goenhub/shared';
import { COMMISSION_LIST_LABELS, COMMISSION_LIST_TYPES } from '@goenhub/shared';
import { ArrowLeft, ArrowRight, CheckCircle2, Circle, FileText, Star } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Screen } from '@/components/layout/Screen';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { useCommissionSetDetail } from '@/hooks/useCommissions';
import {
  useProductCategories,
  type ProductCategoryRecord,
} from '@/hooks/useProductCategories';
import {
  useCatalogItemsForCommissionSet,
  type MergedCatalogItem,
} from '@/hooks/useProductCatalogs';
import { paths } from '@/routes/paths';

import { filterItemsForCategory } from './categoryCatalogLink';
import { CommissionOverridesSection } from './CommissionOverridesSection';
import { CommissionTableViewerDialog } from './CommissionTableViewerDialog';

export function CommissionSetCategoriesPage() {
  const { setId = '' } = useParams<{ setId: string }>();
  const navigate = useNavigate();
  const detail = useCommissionSetDetail(setId);
  const categories = useProductCategories();
  // v3.2.30+: 季節モードではなく、このセットに紐付くカタログの価格を参照する
  const allItems = useCatalogItemsForCommissionSet(setId);

  // すべての hook を unconditional に呼ぶ (React Rules of Hooks)
  const cats = categories.data ?? [];
  const items = allItems.data ?? [];
  const lists = detail.data?.lists ?? null;
  const statusByCategoryId = useMemoStatus(lists, items, cats);

  if (detail.isLoading || categories.isLoading || allItems.isLoading) {
    return (
      <Screen title="読み込み中…">
        <Spinner />
      </Screen>
    );
  }
  if (!detail.data) {
    return (
      <Screen title="報酬セットが見つかりません">
        <Button onClick={() => navigate(paths.commissions)}>
          <ArrowLeft className="size-3.5" /> 一覧に戻る
        </Button>
      </Screen>
    );
  }

  const { set } = detail.data;

  return <PageInner set={set} setId={setId} cats={cats} items={items} statusByCategoryId={statusByCategoryId} navigate={navigate} />;
}

function PageInner({
  set,
  setId,
  cats,
  items,
  statusByCategoryId,
  navigate,
}: {
  set: { name: string; isActive?: boolean };
  setId: string;
  cats: ReadonlyArray<ProductCategoryRecord>;
  items: ReadonlyArray<MergedCatalogItem>;
  statusByCategoryId: Map<string, Status>;
  navigate: ReturnType<typeof useNavigate>;
}) {
  // v3.2.29+: 報酬表ビューア モーダル
  const [viewerCategory, setViewerCategory] = useState<ProductCategoryRecord | null>(
    null,
  );

  return (
    <Screen
      title={set.name}
      description={
        set.isActive
          ? '商品カテゴリーごとに報酬を設定します。このセットは現在の有効セット — 入金時の報酬計算に使用されます。'
          : '商品カテゴリーごとに報酬を設定します。報酬設定一覧画面でこのセットを有効化すると、入金時の報酬計算に使用されます。'
      }
      actions={
        <Button variant="ghost" onClick={() => navigate(paths.commissions)}>
          <ArrowLeft className="size-3.5" /> 一覧に戻る
        </Button>
      }
    >
      <div className="space-y-6">
        {set.isActive && (
          <div className="rounded-md border border-[var(--accent-amber,_#d4a373)] bg-[var(--accent-amber-subtle,_rgba(212,163,115,0.15))] px-3 py-2 inline-flex items-center gap-2 text-[var(--accent-amber,_#d4a373)]">
            <Star className="size-3.5 fill-current" />
            <span className="text-xs font-semibold">このセットは現在「有効」です</span>
          </div>
        )}

        {/* カテゴリーカード一覧 */}
        <div>
          <h3 className="text-sm font-semibold text-[var(--text-primary)] mb-2">
            カテゴリー別 設定状況
          </h3>
          {cats.length === 0 ? (
            <p className="text-xs text-[var(--text-muted)] italic">
              商品カテゴリーがありません。先に「商談ボード &gt; カテゴリー管理」で作成してください。
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {cats.map((cat) => {
                const catalogItemCount = filterItemsForCategory(items, cat).length;
                const status = statusByCategoryId.get(cat.id) ?? {
                  configured: 0,
                  total: 0,
                  badge: 'unset' as const,
                };
                const editorPath = paths.commissionCategoryEditor
                  .replace(':setId', setId)
                  .replace(':categoryId', cat.id);
                return (
                  <div
                    key={cat.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => navigate(editorPath)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') navigate(editorPath);
                    }}
                    className="rounded-md border border-[var(--border)] bg-[var(--bg-surface)] p-4 hover:bg-[var(--bg-hover)] transition-colors space-y-2 cursor-pointer"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-[var(--text-primary)] truncate">
                          {cat.name}
                        </div>
                        <div className="text-[11px] text-[var(--text-muted)] mt-0.5">
                          商品マスタ {catalogItemCount} 件
                        </div>
                      </div>
                      <ArrowRight className="size-4 text-[var(--text-muted)] shrink-0" />
                    </div>
                    {catalogItemCount === 0 ? (
                      <Badge variant="default">対応カタログなし</Badge>
                    ) : status.badge === 'set' ? (
                      <div className="inline-flex items-center gap-1 text-[11px] text-[var(--accent-green,_#3aaa55)]">
                        <CheckCircle2 className="size-3" />
                        設定済み ({status.configured} / {status.total})
                      </div>
                    ) : status.badge === 'partial' ? (
                      <div className="inline-flex items-center gap-1 text-[11px] text-[var(--accent-amber,_#d4a373)]">
                        <Circle className="size-3" />
                        一部設定 ({status.configured} / {status.total})
                      </div>
                    ) : (
                      <div className="inline-flex items-center gap-1 text-[11px] text-[var(--text-muted)]">
                        <Circle className="size-3" />
                        未設定 (0 / {status.total})
                      </div>
                    )}
                    {/* v3.2.29+: 報酬表モーダル起動ボタン (カードクリックの伝播を止める) */}
                    {catalogItemCount > 0 && (
                      <div className="pt-1 border-t border-[var(--border)]">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            setViewerCategory(cat);
                          }}
                          className="w-full justify-center text-[11px] h-7"
                          title="ロール別の報酬表をモーダルで表示 / PDF 出力"
                        >
                          <FileText className="size-3" />
                          報酬表を表示
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* v3.2.9: 「カテゴリ別有効化」は「セット単位の単一有効化」に統合され削除 */}

        {/* v3.2.17: 個別設定 (SA / 社員 オーバーライド) */}
        <CommissionOverridesSection setId={setId} />
      </div>

      {/* v3.2.29+: 報酬表ビューア モーダル */}
      {viewerCategory && (
        <CommissionTableViewerDialog
          open={viewerCategory !== null}
          onOpenChange={(o) => {
            if (!o) setViewerCategory(null);
          }}
          setId={setId}
          category={viewerCategory}
        />
      )}
    </Screen>
  );
}

// ─────────────────────────────────────────────
// 設定状況の集計
// ─────────────────────────────────────────────

type Status = {
  configured: number;
  total: number;
  badge: 'set' | 'partial' | 'unset';
};

function useMemoStatus(
  lists: { [k in CommissionListType]: { items: CommissionItem[] } | null } | null,
  catalogItems: ReadonlyArray<{ id: string; catalogId: string; catalogCategory: string }>,
  cats: ReadonlyArray<{ id: string; name: string; catalogIds?: string[] }>,
) {
  return useMemo(() => {
    const map = new Map<string, Status>();
    if (!lists) return map;
    for (const cat of cats) {
      const linked = cat.catalogIds ?? [];
      const matches =
        linked.length > 0
          ? catalogItems.filter((it) => linked.includes(it.catalogId))
          : catalogItems.filter((it) => it.catalogCategory === cat.name);
      const productIdsInCategory = new Set(matches.map((it) => it.id));
      const total = productIdsInCategory.size * COMMISSION_LIST_TYPES.length;
      if (total === 0) {
        map.set(cat.id, { configured: 0, total: 0, badge: 'unset' });
        continue;
      }
      let configured = 0;
      for (const lt of COMMISSION_LIST_TYPES) {
        const list = lists[lt];
        if (!list) continue;
        for (const item of list.items) {
          if (productIdsInCategory.has(item.productId) && item.value > 0) {
            configured++;
          }
        }
      }
      const badge: Status['badge'] =
        configured === 0 ? 'unset' : configured >= total ? 'set' : 'partial';
      map.set(cat.id, { configured, total, badge });
    }
    return map;
  }, [lists, catalogItems, cats]);
}

// 未使用の警告抑制 (export 用)
export const _COMMISSION_LIST_LABELS = COMMISSION_LIST_LABELS;
